import {
  Inject,
  Injectable,
  OnDestroy,
  Signal,
  WritableSignal,
  inject,
  signal,
} from '@angular/core';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { DOCUMENT } from '@angular/common';
import { ViewportService } from '@ui';

@Injectable({
  providedIn: 'root',
})
export class MobileKeyboardService implements OnDestroy {
  private keyboardShowListener: PluginListenerHandle | null = null;
  private keyboardWillHideListener: PluginListenerHandle | null = null;
  private keyboardDidHideListener: PluginListenerHandle | null = null;
  private isInitialized = false;
  private focusOutCleanupTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly keyboardOpen: WritableSignal<boolean> = signal(false);
  public readonly isKeyboardOpen: Signal<boolean> =
    this.keyboardOpen.asReadonly();

  private readonly viewportService: ViewportService = inject(ViewportService);

  constructor(@Inject(DOCUMENT) private document: Document) {}

  public init(): void {
    if (this.isInitialized || !Capacitor.isNativePlatform()) {
      return;
    }

    this.isInitialized = true;

    this.initListeners().catch((error) => {
      console.warn(
        '[MobileKeyboardService] Failed to initialize keyboard listeners:',
        error,
      );
    });
  }

  private async initListeners(): Promise<void> {
    if (this.viewportService.isNativeAndroid()) {
      this.keyboardShowListener = await Keyboard.addListener(
        'keyboardDidShow',
        (info) => this.handleKeyboardShow(info.keyboardHeight || 0),
      );
    } else {
      this.keyboardShowListener = await Keyboard.addListener(
        'keyboardWillShow',
        (info) => this.handleKeyboardShow(info.keyboardHeight || 0),
      );

      this.keyboardWillHideListener = await Keyboard.addListener(
        'keyboardWillHide',
        () => this.handleKeyboardHide(),
      );
    }

    this.keyboardDidHideListener = await Keyboard.addListener(
      'keyboardDidHide',
      () => this.handleKeyboardHide(),
    );

    this.document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.document.addEventListener('focusout', this.onFocusOut);
  }

  private handleKeyboardShow(keyboardHeight: number): void {
    this.clearFocusOutTimer();
    const root = this.document.documentElement;
    root.style.setProperty('--keyboard-height', `${keyboardHeight}px`);
    this.document.body.classList.add('keyboard-open');
    this.keyboardOpen.set(true);

    setTimeout(() => {
      const active = this.document.activeElement;
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement
      ) {
        active.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  }

  private handleKeyboardHide(): void {
    this.clearFocusOutTimer();
    const root = this.document.documentElement;
    root.style.removeProperty('--keyboard-height');
    this.document.body.classList.remove('keyboard-open');
    this.keyboardOpen.set(false);
  }

  private onVisibilityChange = (): void => {
    if (
      this.document.visibilityState === 'visible' &&
      this.isKeyboardClassPresent()
    ) {
      this.handleKeyboardHide();
    }
  };

  private onFocusOut = (): void => {
    if (!this.isKeyboardClassPresent()) return;

    this.clearFocusOutTimer();
    this.focusOutCleanupTimer = setTimeout(() => {
      this.focusOutCleanupTimer = null;
      const active = this.document.activeElement;
      const isInputFocused =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active as HTMLElement)?.isContentEditable;

      if (!isInputFocused && this.isKeyboardClassPresent()) {
        this.handleKeyboardHide();
      }
    }, 300);
  };

  private isKeyboardClassPresent(): boolean {
    return this.document.body.classList.contains('keyboard-open');
  }

  private clearFocusOutTimer(): void {
    if (this.focusOutCleanupTimer !== null) {
      clearTimeout(this.focusOutCleanupTimer);
      this.focusOutCleanupTimer = null;
    }
  }

  public ngOnDestroy(): void {
    void this.keyboardShowListener?.remove();
    this.keyboardShowListener = null;

    void this.keyboardWillHideListener?.remove();
    this.keyboardWillHideListener = null;

    void this.keyboardDidHideListener?.remove();
    this.keyboardDidHideListener = null;

    this.document.removeEventListener(
      'visibilitychange',
      this.onVisibilityChange,
    );
    this.document.removeEventListener('focusout', this.onFocusOut);
    this.clearFocusOutTimer();

    const root = this.document.documentElement;
    root.style.removeProperty('--keyboard-height');
    this.document.body.classList.remove('keyboard-open');

    this.isInitialized = false;
  }
}
